<script setup>
/**
 * Field report G2.2: whether the assistant follows up a customer who goes quiet in the middle of a
 * task. OFF until she turns it on (owner decision D-2): each follow-up is a WhatsApp message the
 * business may pay for, and the description says so.
 *
 * The value is the BFF's `followUps` exactly (`config-bag.ts` `FollowUpsDTO`). The waits are the
 * only ones the server accepts (`helpers/sending-hours.js`), so a choice here can never be refused;
 * the one rule a save can still fail (her own words chosen, none written) comes back as `message`.
 */
import { computed } from 'vue';

import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import WasloInput from './WasloInput.vue';
import WasloSelect from './WasloSelect.vue';
import { useWasloI18n } from '../i18n.js';
import {
  FIRST_AFTER_MINUTES,
  MAX_FOLLOW_UP_CHARS,
  SECOND_AFTER_MINUTES,
  defaultFollowUps,
} from '../helpers/sending-hours.js';

const props = defineProps({
  /** `FollowUpsDTO`. */
  modelValue: { type: Object, default: null },
  /** "Your customers write in" (P-7.1): only the boxes for her languages are shown. */
  languages: { type: String, default: 'both' },
  /** The save's sentence for this section, if it refused one. */
  message: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();

const value = computed(() => props.modelValue ?? defaultFollowUps());
const write = changes =>
  emit('update:modelValue', { ...value.value, ...changes });

// Ids carry a prefix because `RadioCard` writes its id onto a real `<input>`.
const switchOptions = computed(() => [
  {
    id: 'followups_off',
    label: t('WASLO.FOLLOW_UPS.OFF'),
    description: t('WASLO.FOLLOW_UPS.OFF_HINT'),
  },
  {
    id: 'followups_on',
    label: t('WASLO.FOLLOW_UPS.ON'),
    description: t('WASLO.FOLLOW_UPS.ON_HINT'),
  },
]);
const switchChoice = computed(() =>
  value.value.on ? 'followups_on' : 'followups_off'
);
const selectSwitch = id => {
  if (id === 'followups_on' || id === 'followups_off') {
    write({ on: id === 'followups_on' });
  }
};

const countOptions = computed(() => [
  { value: '1', label: t('WASLO.FOLLOW_UPS.ONCE') },
  { value: '2', label: t('WASLO.FOLLOW_UPS.TWICE') },
]);
const firstOptions = computed(() =>
  FIRST_AFTER_MINUTES.map(minutes => ({
    value: String(minutes),
    label: t(`WASLO.FOLLOW_UPS.FIRST_AFTER.M${minutes}`),
  }))
);
const secondOptions = computed(() =>
  SECOND_AFTER_MINUTES.map(minutes => ({
    value: String(minutes),
    label: t(`WASLO.FOLLOW_UPS.SECOND_AFTER.M${minutes}`),
  }))
);

const wordingOptions = computed(() => [
  {
    id: 'wording_question',
    label: t('WASLO.FOLLOW_UPS.WORDING.QUESTION'),
    description: t('WASLO.FOLLOW_UPS.WORDING.QUESTION_HINT'),
  },
  {
    id: 'wording_own',
    label: t('WASLO.FOLLOW_UPS.WORDING.OWN'),
    description: t('WASLO.FOLLOW_UPS.WORDING.OWN_HINT'),
  },
]);
const selectWording = id => {
  if (id === 'wording_question') write({ wording: 'question' });
  if (id === 'wording_own') write({ wording: 'own' });
};

const showsEnglish = computed(() => props.languages !== 'ar');
const showsArabic = computed(() => props.languages !== 'en');
const wordsOrNull = text => (text === '' ? null : text);
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-follow-ups>
    <SettingsHeader
      :heading="t('WASLO.FOLLOW_UPS.HEADING')"
      :description="t('WASLO.FOLLOW_UPS.DESCRIPTION')"
    />
    <div class="flex flex-col gap-3">
      <RadioCard
        v-for="option in switchOptions"
        :id="option.id"
        :key="option.id"
        :label="option.label"
        :description="option.description"
        :is-active="switchChoice === option.id"
        :data-waslo-follow-ups-option="option.id"
        @select="selectSwitch"
      />
    </div>

    <template v-if="value.on">
      <div class="flex flex-wrap items-end gap-3">
        <WasloSelect
          :model-value="String(value.nudges)"
          :label="t('WASLO.FOLLOW_UPS.TIMES')"
          :options="countOptions"
          data-waslo-follow-ups-count
          @update:model-value="nudges => write({ nudges: Number(nudges) })"
        />
        <WasloSelect
          :model-value="String(value.firstAfterMinutes)"
          :label="t('WASLO.FOLLOW_UPS.FIRST')"
          :options="firstOptions"
          data-waslo-follow-ups-first
          @update:model-value="
            minutes => write({ firstAfterMinutes: Number(minutes) })
          "
        />
        <WasloSelect
          v-if="value.nudges === 2"
          :model-value="String(value.secondAfterMinutes)"
          :label="t('WASLO.FOLLOW_UPS.SECOND')"
          :options="secondOptions"
          data-waslo-follow-ups-second
          @update:model-value="
            minutes => write({ secondAfterMinutes: Number(minutes) })
          "
        />
      </div>

      <div class="flex flex-col gap-3">
        <RadioCard
          v-for="option in wordingOptions"
          :id="option.id"
          :key="option.id"
          :label="option.label"
          :description="option.description"
          :is-active="`wording_${value.wording}` === option.id"
          :data-waslo-follow-ups-wording="option.id"
          @select="selectWording"
        />
      </div>

      <div class="flex flex-col gap-3">
        <WasloInput
          v-if="showsEnglish"
          :model-value="value.textEn ?? ''"
          :label="t('WASLO.FOLLOW_UPS.WORDS_EN')"
          :hint="t('WASLO.FOLLOW_UPS.WORDS_HINT')"
          :placeholder="t('WASLO.FOLLOW_UPS.WORDS_PLACEHOLDER_EN')"
          :max-length="MAX_FOLLOW_UP_CHARS"
          data-waslo-follow-ups-words="en"
          @update:model-value="text => write({ textEn: wordsOrNull(text) })"
        />
        <WasloInput
          v-if="showsArabic"
          :model-value="value.textAr ?? ''"
          :label="t('WASLO.FOLLOW_UPS.WORDS_AR')"
          :hint="t('WASLO.FOLLOW_UPS.WORDS_HINT')"
          :placeholder="t('WASLO.FOLLOW_UPS.WORDS_PLACEHOLDER_AR')"
          :max-length="MAX_FOLLOW_UP_CHARS"
          data-waslo-follow-ups-words="ar"
          @update:model-value="text => write({ textAr: wordsOrNull(text) })"
        />
      </div>

      <p class="mb-0 text-sm text-n-slate-11" data-waslo-follow-ups-rules>
        {{ t('WASLO.FOLLOW_UPS.RULES') }}
      </p>
    </template>

    <p
      v-if="message"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-follow-ups-error
    >
      {{ message }}
    </p>
  </div>
</template>
